import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { RECORD_RESEARCH_FEEDBACK, RESEARCH_ASK } from '../../graphql';
import { ResearchAskResponse, ResearchHit } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { TopicFilter, groupTopics } from '../cms/TopicFilter';
import { useSeesExtractedDetail } from '../settings/supportAccess';

/**
 * Prism AI → Research ("tinycms UX optimization review v6", Prototype v6): ask
 * the archive a question (answers cite the articles they came from), with
 * starter questions under the box; below, the latest facts, filterable by
 * category. Only atomized articles feed it, so nothing here is new reporting.
 *
 * The Facts list, its counts and topic filter, and per-source fact counts are
 * TinyCMS technical support's only (backlog e3df0116): everyone else gets the
 * answer and the articles it cites, and the API sends them nothing more.
 */

const RESEARCH_OVERVIEW = gql`
  query ResearchOverview($accountId: ID!, $category: String, $limit: Int) {
    researchOverview(accountId: $accountId, category: $category, limit: $limit) {
      articleCount
      atomizedCount
      pagesRead
      factCount
      categories
      topics {
        name
        count
      }
      suggestions
      facts {
        fact
        date
        publishedDate
        topicId
        topicTitle
        tags
      }
    }
  }
`;

// Only whether Research is set up and on; never the keys or where it runs.
const RESEARCH_AVAILABLE = gql`
  query ResearchAvailable($accountId: ID!) {
    researchSettings(accountId: $accountId) {
      id
      searchApiKey
      isActive
    }
  }
`;

interface OverviewFact {
  fact: string;
  date?: string | null;
  publishedDate?: string | null;
  topicId: string;
  topicTitle?: string | null;
  tags: string[];
}
interface Overview {
  articleCount: number;
  atomizedCount: number;
  /** Live pages Prism AI has read: Research answers from them too. */
  pagesRead?: number;
  factCount: number;
  categories: string[];
  /** The same topics with how many read articles each has. */
  topics?: Array<{ name: string; count: number }>;
  suggestions: string[];
  facts: OverviewFact[];
}

/** A few topics are chips (Prototype v15: All / MLB / NCAAF); more use Content's dropdown. */
const CHIP_TOPICS = 4;

interface Source {
  n: number;
  title: string;
  topicId?: string | null;
  url?: string | null;
  facts: number;
}
interface Answer {
  question: string;
  text: string;
  sources: Source[];
  /** Citation marker (C3) → source number. */
  cite: Record<string, number>;
  followups: string[];
  queryId?: string | null;
  feedback?: 'up' | 'down';
  /** After No: "What was off?" is open until a reason or Skip. */
  askingWhy?: boolean;
  error?: boolean;
}

/** "What was off?" after a No (Prototype, backlog 6d9eddf6). */
const FEEDBACK_REASONS: { id: string; label: string }[] = [
  { id: 'wrong_fact', label: 'A fact is wrong or out of date' },
  { id: 'missed_article', label: 'It missed an article' },
  { id: 'not_answered', label: 'It didn’t answer the question' },
];

const FACTS_SHOWN = 12;

const shortDate = (fact: OverviewFact) => {
  const raw = fact.date && /^\d{4}-\d{2}-\d{2}/.test(fact.date) ? fact.date : fact.publishedDate;
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    : '';
};

/** One numbered source per article; each citation points at its article. */
function toSources(hits: ResearchHit[]): Pick<Answer, 'sources' | 'cite'> {
  const sources: Source[] = [];
  const cite: Record<string, number> = {};
  for (const h of hits) {
    const key = h.topicId || h.url || h.articleTitle || '';
    let s = sources.find((x) => (x.topicId || x.url || x.title) === key);
    if (!s) {
      s = {
        n: sources.length + 1,
        title: h.articleTitle || h.headline || 'Untitled article',
        topicId: h.topicId,
        url: h.url,
        facts: 0,
      };
      sources.push(s);
    }
    s.facts += 1;
    if (h.citationId) cite[h.citationId.replace(/^C?/, 'C')] = s.n;
  }
  return { sources, cite };
}

function AnswerText({ text, cite }: { text: string; cite: Record<string, number> }) {
  const parts = text.split(/(\[C?\d+\])/g);
  return (
    <p className="rs-answer-text">
      {parts.map((part, i) => {
        const m = part.match(/^\[C?(\d+)\]$/);
        if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
        const n = cite[`C${m[1]}`];
        return n ? (
          <sup key={i} className="rs-cite">
            [{n}]
          </sup>
        ) : null;
      })}
    </p>
  );
}

export function ResearchLayout() {
  const { accountId } = useAccount();
  const seesExtracted = useSeesExtractedDetail(accountId);
  const client = useApolloClient();
  const [category, setCategory] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [recordFeedback] = useMutation(RECORD_RESEARCH_FEEDBACK);
  const sessionId = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now())
  );
  const sessionSeq = useRef(0);

  const { data: availability, loading: availabilityLoading } = useQuery<{
    researchSettings: { searchApiKey?: string | null; isActive: boolean } | null;
  }>(RESEARCH_AVAILABLE, { variables: { accountId }, skip: !accountId });
  const available = Boolean(
    availability?.researchSettings?.searchApiKey && availability.researchSettings.isActive !== false
  );

  const { data, loading } = useQuery<{ researchOverview: Overview }>(RESEARCH_OVERVIEW, {
    variables: { accountId, category, limit: FACTS_SHOWN },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const overview = data?.researchOverview;
  // Keep the topic filter steady while a filtered list loads.
  const [allTopics, setAllTopics] = useState<Array<{ name: string; count: number }>>([]);
  const unfilteredTopics =
    overview?.topics || overview?.categories.map((name) => ({ name, count: 0 })) || [];
  if (
    overview &&
    !category &&
    unfilteredTopics.map((t) => t.name).join('|') !== allTopics.map((t) => t.name).join('|')
  ) {
    setAllTopics(unfilteredTopics);
  }
  const allCategories = allTopics.map((t) => t.name);
  // Many topics (a feed's sections): Content's searchable dropdown, not a wall of
  // chips (QA round 7: nine chips over three lines).
  const topicGroups = useMemo(() => groupTopics(allTopics, (name) => name), [allTopics]);

  const ask = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || asking || !accountId) return;
      // The question moves to the answer card; the box is ready for the next (Prototype).
      setInput('');
      setAsking(true);
      try {
        sessionSeq.current += 1;
        const { data: res } = await client.query<ResearchAskResponse>({
          query: RESEARCH_ASK,
          variables: {
            accountId,
            query: q,
            lang: 'en',
            sessionId: sessionId.current,
            sessionSeq: sessionSeq.current,
          },
          fetchPolicy: 'network-only',
        });
        const r = res.researchAsk;
        const cited = r.citations.length ? r.citations : r.hits;
        // A non-answer shows the server's fixed line (one policy with the
        // reader chat); the older wording stays for a server without one.
        setAnswer({
          question: q,
          text: r.answer
            ? r.answer
            : r.message ||
              (r.hits.length
                ? 'These articles match, but an answer couldn’t be written right now. Try again in a minute.'
                : 'Nothing Prism AI has read answers that. Try asking it another way.'),
          ...toSources(cited),
          followups: r.followups || [],
          queryId: r.answer ? r.queryId : null,
        });
      } catch (err) {
        setAnswer({
          question: q,
          text: err instanceof Error ? err.message : 'That didn’t work. Try again.',
          sources: [],
          cite: {},
          followups: [],
          error: true,
        });
      } finally {
        setAsking(false);
      }
    },
    [accountId, asking, client]
  );

  // The vote is saved straight away; a No then asks what was off, and the
  // reason (if one is picked) is saved with it.
  const feedback = async (value: 'up' | 'down', reason: string | null = null) => {
    if (!answer?.queryId || !accountId) return;
    setAnswer({ ...answer, feedback: value, askingWhy: value === 'down' && !reason });
    try {
      await recordFeedback({
        variables: { accountId, queryId: answer.queryId, feedback: value, reason },
      });
    } catch {
      // Not worth bothering the person about.
    }
  };

  // Starters until there's an answer; then its follow-ups sit inside the card,
  // under "Ask next" (Prototype, backlog ec68551f).
  const chips = useMemo(
    () => (answer ? [] : (overview?.suggestions || []).slice(0, 3)),
    [answer, overview]
  );
  const next = useMemo(
    () => (answer?.followups || []).filter((q) => q !== answer?.question).slice(0, 3),
    [answer]
  );

  if (!accountId || availabilityLoading) return <div className="rs" />;

  // Nothing read yet (Prototype v7): one card on what Prism AI does, in place
  // of a question box with nothing to search. Customers see no fact count, so
  // for them it's whether any article has been read.
  // Pages count as read (backlog 60b346c4): a pages-only site still gets the question box.
  if (
    overview &&
    !category &&
    (seesExtracted ? overview.factCount === 0 : overview.atomizedCount === 0 && !overview.pagesRead)
  ) {
    return (
      <div className="rs">
        <div className="rc-empty" style={{ marginTop: 8 }}>
          <div className="rc-empty-title">
            {seesExtracted ? 'No facts yet' : 'Nothing read yet'}
          </div>
          <p>
            {seesExtracted
              ? 'Prism AI breaks each article into short, sourced facts you can search, ask questions of and reuse; they appear here as each article is read.'
              : 'Prism AI reads each article so you can ask your archive questions. Ask here once it has read a few.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rs">
      {available ? (
        <>
          <form
            className="rs-ask"
            onSubmit={(e) => {
              e.preventDefault();
              void ask(input);
            }}
          >
            <input
              className="rs-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask your archive a question"
              aria-label="Ask your archive a question"
            />
            <button
              type="submit"
              className="ink-btn ink-btn-primary rs-ask-btn"
              disabled={asking || !input.trim()}
            >
              {asking ? 'Asking…' : 'Ask'}
            </button>
          </form>
          {chips.length ? (
            <div className="rs-chips">
              {chips.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="rs-chip"
                  disabled={asking}
                  onClick={() => void ask(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          ) : null}

          {answer ? (
            <section className="rs-answer" aria-live="polite">
              <div className="rs-answer-q">{answer.question}</div>
              {answer.error ? (
                <p className="rs-answer-text" style={{ color: 'var(--ink-red)' }}>
                  {answer.text}
                </p>
              ) : (
                <AnswerText text={answer.text} cite={answer.cite} />
              )}
              {answer.sources.length ? (
                <div className="rs-sources">
                  {answer.sources.map((s) => {
                    const body = (
                      <>
                        <span className="rs-source-n">[{s.n}]</span>
                        <span className="rs-source-title">{s.title}</span>
                        {seesExtracted ? (
                          <span className="rs-source-count">
                            {s.facts} {s.facts === 1 ? 'fact' : 'facts'}
                          </span>
                        ) : null}
                      </>
                    );
                    return s.topicId ? (
                      <Link key={s.n} className="rs-source" to={`/content/${s.topicId}`}>
                        {body}
                      </Link>
                    ) : s.url ? (
                      <a
                        key={s.n}
                        className="rs-source"
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {body}
                      </a>
                    ) : (
                      <div key={s.n} className="rs-source">
                        {body}
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="rs-answer-foot">
                <span>
                  {seesExtracted
                    ? 'Built only from facts in your articles. Nothing here is new reporting.'
                    : overview?.pagesRead
                      ? 'Built only from articles and pages Prism AI has read. Nothing here is new reporting.'
                      : 'Built only from articles Prism AI has read. Nothing here is new reporting.'}
                </span>
                {answer.queryId && !answer.askingWhy ? (
                  <span className="rs-helpful">
                    {answer.feedback ? (
                      'Thanks. This helps us improve Prism AI.'
                    ) : (
                      <>
                        Helpful?
                        <button type="button" onClick={() => void feedback('up')}>
                          Yes
                        </button>
                        <button type="button" onClick={() => void feedback('down')}>
                          No
                        </button>
                      </>
                    )}
                  </span>
                ) : null}
              </div>
              {answer.askingWhy ? (
                <div className="rs-why">
                  <div className="rs-why-head">What was off?</div>
                  <div className="rs-chips">
                    {FEEDBACK_REASONS.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        className="rs-chip"
                        onClick={() => void feedback('down', r.id)}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="rs-why-skip"
                    onClick={() => setAnswer({ ...answer, askingWhy: false })}
                  >
                    Skip
                  </button>
                </div>
              ) : null}
              {next.length ? (
                <div className="rs-next">
                  <div className="rs-next-head">Ask next, from the same articles</div>
                  <div className="rs-next-list">
                    {next.map((q) => (
                      <button
                        key={q}
                        type="button"
                        className="rs-next-item"
                        disabled={asking}
                        onClick={() => void ask(q)}
                      >
                        <span aria-hidden="true">↳</span>
                        <span>{q}</span>
                        <span aria-hidden="true">→</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}
        </>
      ) : (
        <div className="rc-empty" style={{ marginTop: 8 }}>
          <div className="rc-empty-title">
            {availability?.researchSettings?.isActive === false
              ? 'Research is turned off'
              : 'Research isn’t available yet'}
          </div>
          <p>
            {availability?.researchSettings?.isActive === false
              ? 'Turn it back on in Settings → Research to ask your archive questions.'
              : seesExtracted
                ? 'Asking questions isn’t ready on this site yet. Your facts are below.'
                : 'Asking questions isn’t ready on this site yet.'}
          </p>
        </div>
      )}

      {seesExtracted ? (
        <section className="rs-facts">
          <div className="rs-facts-head">
            <h2 className="rs-h2">Facts</h2>
            {allCategories.length > CHIP_TOPICS ? (
              <TopicFilter
                groups={topicGroups}
                value={category ? category.trim().toLowerCase() : null}
                onChange={(g) => setCategory(g ? g.label : null)}
              />
            ) : allCategories.length > 1 ? (
              <div className="rs-filters" role="radiogroup" aria-label="Category">
                {[null, ...allCategories].map((c) => (
                  <button
                    key={c ?? 'all'}
                    type="button"
                    role="radio"
                    aria-checked={category === c}
                    className="rs-filter"
                    data-active={category === c ? 'true' : undefined}
                    onClick={() => setCategory(c)}
                  >
                    {c ?? 'All'}
                  </button>
                ))}
              </div>
            ) : null}
            {/* How much of the archive is read sits in the Prism AI header (v15);
              here, how many of the facts are shown. */}
            {overview?.facts.length ? (
              <span className="rs-facts-count">
                Latest {overview.facts.length.toLocaleString('en-US')} of{' '}
                {overview.factCount.toLocaleString('en-US')}
              </span>
            ) : null}
          </div>
          {overview?.facts.length ? (
            <div className="rs-fact-list">
              {overview.facts.map((f, i) => (
                <div className="rs-fact" key={`${f.topicId}-${i}`}>
                  <div className="rs-fact-body">
                    <div className="rs-fact-text">{f.fact}</div>
                    <div className="rs-fact-meta">
                      {f.tags.map((t) => (
                        <span key={t} className="rs-tag">
                          {t}
                        </span>
                      ))}
                      <Link to={`/content/${f.topicId}`} className="rs-fact-src">
                        {f.topicTitle || 'Untitled article'}
                      </Link>
                    </div>
                  </div>
                  <span className="rs-fact-date">{shortDate(f)}</span>
                </div>
              ))}
            </div>
          ) : loading && !overview ? null : (
            <p className="rs-none">
              No facts yet. Each article is broken into facts once Prism AI reads it.
            </p>
          )}
        </section>
      ) : null}
    </div>
  );
}
